import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../data/blogPosts';

const Blog = () => {
  const [visiblePosts, setVisiblePosts] = useState(3); // Start with 3 posts

  // Helper function to convert month names to numbers for sorting
  const getMonthNumber = (monthName) => {
    const months = {
      'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
      'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
    };
    return months[monthName] || 0;
  };

  // Sort blog posts by date (most recent first)
  const sortedBlogPosts = [...blogPosts].sort((a, b) => {
    const [aMonth, aDay] = a.date.split(' ');
    const [bMonth, bDay] = b.date.split(' ');
    
    // Sort by year if available (assuming current year if not specified)
    const currentYear = new Date().getFullYear();
    const aYear = parseInt(aDay.includes(',') ? aDay.split(',')[1].trim() : currentYear);
    const bYear = parseInt(bDay.includes(',') ? bDay.split(',')[1].trim() : currentYear);
    
    if (aYear !== bYear) {
      return bYear - aYear; // Most recent year first
    }
    
    // If same year, sort by month
    const aMonthNum = getMonthNumber(aMonth);
    const bMonthNum = getMonthNumber(bMonth);
    
    if (aMonthNum !== bMonthNum) {
      return bMonthNum - aMonthNum; // Most recent month first
    }
    
    // If same month, sort by day
    const aDayNum = parseInt(aDay.replace(/\D/g, ''));
    const bDayNum = parseInt(bDay.replace(/\D/g, ''));
    
    return bDayNum - aDayNum; // Most recent day first
  });

  // Get only the visible posts
  const displayedPosts = sortedBlogPosts.slice(0, visiblePosts);

  // Handle loading more posts
  const handleLoadMore = () => {
    setVisiblePosts(prev => Math.min(prev + 3, sortedBlogPosts.length));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-center mb-4">ANCILE Blog</h1>
        <p className="text-xl text-center text-gray-600 mb-12">Insights and updates on data security, privacy, and compliance</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedPosts.map(post => (
            <div key={post.id} className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500" />
              <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 h-full flex flex-col">
              <img 
                src={post.image} 
                alt={post.title} 
                className="w-full object-contain h-64" 
              />
              <div className="p-6 flex-grow flex flex-col">
                <div className="text-sm text-gray-500 mb-2 flex items-center gap-2">
                  <span>{post.date}</span>
                  <span className="mx-1">•</span>
                  <span className="flex items-center gap-1">
                    by {post.authors.map((author, index) => (
                      <span key={author.name} className="flex items-center">
                        {index > 0 && <span>, </span>}
                        <span>{author.name}</span>
                        <a 
                          href={author.linkedin} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-blue-600 hover:text-blue-800 ml-1"
                          aria-label={`${author.name}'s LinkedIn profile`}
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                          </svg>
                        </a>
                      </span>
                    ))}
                  </span>
                </div>
                <h2 className="text-xl font-semibold mb-2">{post.title}</h2>
                <p className="text-gray-600 mb-4 flex-grow">{post.excerpt}</p>
                <Link 
                  to={`/blog/${post.slug}`} 
                  className="text-blue-500 font-medium hover:text-blue-600 mt-auto"
                >
                  Read More →
                </Link>
              </div>
              </div>
            </div>
          ))}
        </div>
        
        {visiblePosts < sortedBlogPosts.length && (
          <div className="text-center mt-12">
            <button 
              onClick={handleLoadMore}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Load More
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog; 